import { isApolloError, gql } from '@apollo/client';
import axios from 'axios';

import clientApollo from './clientApollo';

import { API_KEY, URL } from '~/common/helpers/environment';
import {
  DataStreaming,
  DataStreamingSavePost,
  DataStreamingSavePut,
  ExporterTypesResponse
} from '~/entities/DataStreaming';

type DataStreamingConfigSaveData = {
  realm: string;
  dataStreaming: DataStreaming;
  id?: string;
};

type SaveDataStreamingConfigResult = {
  createDataStreamingConfig: boolean;
};

type UpdatedDataStreamingConfigResult = {
  updateDataStreamingConfig: boolean;
};

type DataStreamingConfigFetchData = {
  realm: string;
};

type DataStreamingConfigFetchAllResult = {
  getDataStreamingConfig: DataStreaming;
};

type RemoveDataStreamingConfigData = {
  realm: string;
  id: string;
};

type RemoveDataStreamingConfigResult = {
  deleteDataStreamingConfig: boolean;
};
const dataStreamingConfig = {
  getDataStreamingConfig: async ({ realm }: DataStreamingConfigFetchData) => {
    try {
      const result =
        await clientApollo.query<DataStreamingConfigFetchAllResult>({
          query: gql`
            query getDataStreamingConfig($realm: String!) {
              getDataStreamingConfig(realm: $realm)
            }
          `,
          variables: {
            realm
          }
        });

      return result.data?.getDataStreamingConfig;
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  },
  createDataStreamingConfig: async ({
    realm,
    dataStreaming
  }: DataStreamingConfigSaveData) => {
    try {
      const dataStreamingWithRealm: DataStreamingSavePost = {
        realm,
        exporter: dataStreaming.exporter.toLocaleLowerCase(),
        config: {
          host: dataStreaming.config.host,
          apiKey: dataStreaming?.config?.apiKey || ''
        },
        contexts: dataStreaming.contexts
      };

      const result = await clientApollo.mutate<SaveDataStreamingConfigResult>({
        mutation: gql`
          mutation createDataStreamingConfig($dataStreaming: JSON) {
            createDataStreamingConfig(dataStreaming: $dataStreaming)
          }
        `,
        variables: {
          dataStreaming: dataStreamingWithRealm
        }
      });
      return result.data?.createDataStreamingConfig;
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  },
  updateDataStreamingConfig: async ({
    realm,
    dataStreaming
  }: DataStreamingConfigSaveData) => {
    try {
      const dataStreamingWithRealm: DataStreamingSavePut = {
        realm,
        id: dataStreaming.id,
        exporter: dataStreaming.exporter.toLocaleLowerCase(),
        config: {
          host: dataStreaming.config.host,
          apiKey: dataStreaming?.config?.apiKey
        },
        contexts: dataStreaming.contexts
      };

      const result =
        await clientApollo.mutate<UpdatedDataStreamingConfigResult>({
          mutation: gql`
            mutation updateDataStreamingConfig($dataStreaming: JSON) {
              updateDataStreamingConfig(dataStreaming: $dataStreaming)
            }
          `,
          variables: {
            dataStreaming: dataStreamingWithRealm
          }
        });
      return result.data?.updateDataStreamingConfig;
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  },
  removeDataStreamingConfig: async ({ id }: RemoveDataStreamingConfigData) => {
    try {
      const result = await clientApollo.mutate<RemoveDataStreamingConfigResult>(
        {
          mutation: gql`
            mutation deleteDataStreamingConfig($id: String!) {
              deleteDataStreamingConfig(id: $id)
            }
          `,
          variables: {
            id
          }
        }
      );
      return result.data?.deleteDataStreamingConfig;
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  },

  // MOCK
  getDataStreamingConfigMOCK: async () => {
    try {
      return {
        content: [
          {
            id: '0085e0c9-0a6d-44d6-8f6a-3e160f279a6a',
            realm: 'staging',
            exporter: 'DATADOG',
            config: {
              host: 'us5.datadoghq.com',
              apiKey: ''
            },
            contexts: ['LOGS', 'ERROR'],
            status: {
              status: 'DOWN',
              lastCheckTime: '2025-03-26T00:11:40.526276313Z',
              contextStatus: {
                LOGS: 'DOWN'
              }
            }
          }
        ]
      };
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  },

  getExporterTypes: async ({
    token
  }: {
    token: string;
  }): Promise<ExporterTypesResponse> => {
    try {
      const response = await axios.get<ExporterTypesResponse>(
        `${URL}/monitor/api/v1/telemetry/exporters/types`,
        {
          headers: {
            apiKey: API_KEY,
            Authorization: token
          }
        }
      );

      return response.data;
    } catch (e) {
      if (e instanceof Error) throw Error(e.message);
      throw e;
    }
  }
};

export default dataStreamingConfig;
